// Persistent, non-dismissable banner shown on every page while the account is
// filled with sample data, plus the action that clears it and hands back an
// empty account ready to set up.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FlaskConical, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { friendlyError } from "@/lib/friendlyError";
import { useClearSampleData, useSampleData } from "@/hooks/useSampleData";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";

export function SampleDataBanner() {
  const { isSampleData } = useSampleData();
  const { role } = useAuth();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const clear = useClearSampleData();
  const { toast } = useToast();
  const navigate = useNavigate();

  if (!isSampleData) return null;

  const isOwner = role === "super_admin";

  const handleClear = async () => {
    try {
      await clear.mutateAsync();
      setConfirmOpen(false);
      toast({
        title: "Sample data cleared",
        description: "Your account is empty and ready to set up.",
      });
      navigate("/dashboard");
    } catch (e) {
      toast({
        title: "Could not clear the sample data",
        description: friendlyError(e),
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <div className="w-full bg-nursly-amber text-white">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
          <p className="flex items-center gap-2 text-sm font-medium font-body">
            <FlaskConical className="h-4 w-4 shrink-0" />
            You are exploring with sample data. Nothing here is real yet.
          </p>
          {isOwner && (
            <Button
              size="sm"
              variant="secondary"
              className="h-9 bg-white text-nursly-amber hover:bg-white/90"
              onClick={() => setConfirmOpen(true)}
            >
              Clear this and set up my own nursery
            </Button>
          )}
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear the sample data?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes every example child, member of staff, rota, register and
              compliance entry. You will be left with a completely empty account and we
              will walk you through setting up your own nursery. This cannot be undone,
              but nothing real is lost because none of the sample information belongs to
              your nursery.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={clear.isPending}>Keep exploring</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleClear();
              }}
              disabled={clear.isPending}
            >
              {clear.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Clearing
                </>
              ) : (
                "Yes, clear it"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
